import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Drawer,
  Button,
  Switch,
  Avatar,
  Typography,
  Space,
  Divider,
  Flex,
  theme as antdThemeApi,
} from 'antd';
import {
  BankOutlined,
  UserOutlined,
  RiseOutlined,
  SyncOutlined,
  BarChartOutlined,
  DollarOutlined,
  VideoCameraOutlined,
  MailOutlined,
  RadarChartOutlined,
  FormOutlined,
  CreditCardOutlined,
  BookOutlined,
  ToolOutlined,
  ReadOutlined,
  PictureOutlined,
  NotificationOutlined,
  StopOutlined,
  RobotOutlined,
  TeamOutlined,
  FileSearchOutlined,
  MenuOutlined,
  LogoutOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { PERMISSIONS } from '../utils/permissions';
import { useTheme, SIDEBAR_WIDTH } from '../context/ThemeContext';

const { Sider } = Layout;
const { Text, Title } = Typography;

const SKYLAR_THREADS_URL =
  'https://project-skylar.vercel.app/internal/skylar/threads?key=3b2de87be2ec210364c8368eb94294f8c3fd31151dbdff8d8f43fc8061009d3d';

const ICON_MAP = {
  'workspace-info': <BankOutlined />,
  'user-sso-login': <UserOutlined />,
  'customer-statistics': <RiseOutlined />,
  'workflow-task-analytics': <SyncOutlined />,
  'agent-analytics': <BarChartOutlined />,
  'credit-analytics': <DollarOutlined />,
  'meetings-analytics': <VideoCameraOutlined />,
  'email-metrics': <MailOutlined />,
  'trends-pipeline': <RadarChartOutlined />,
  'manage-company-lists': <FormOutlined />,
  'vendor-credits': <CreditCardOutlined />,
  pinboard: <BookOutlined />,
  'data-correction': <ToolOutlined />,
  'data-correction-logs': <ReadOutlined />,
  'public-images': <PictureOutlined />,
  'whats-new': <NotificationOutlined />,
  'signup-blacklist': <StopOutlined />,
  'skylar-threads': <RobotOutlined />,
  'manage-users': <TeamOutlined />,
  'audit-logs': <FileSearchOutlined />,
};

const allMenuItems = [
  { id: 'workspace-info', label: 'Workspace Information', public: true, path: '/workspace-info' },
  { id: 'user-sso-login', label: 'User Information/SSO', permission: PERMISSIONS.USER_SSO, path: '/user-sso-login' },
  { id: 'customer-statistics', label: 'Customer Statistics', public: true, path: '/customer-statistics' },
  { id: 'workflow-task-analytics', label: 'Workflow Task Analytics', public: true, path: '/workflow-task-analytics' },
  { id: 'agent-analytics', label: 'GTM Agent Analytics', public: true, path: '/agent-analytics' },
  { id: 'credit-analytics', label: 'Credit Analytics', public: true, path: '/credit-analytics' },
  { id: 'meetings-analytics', label: 'Meetings Analytics', public: true, path: '/meetings-analytics' },
  { id: 'email-metrics', label: 'Email Metrics', public: true, path: '/email-metrics' },
  { id: 'trends-pipeline', label: 'Trends Pipeline', public: true, path: '/trends-pipeline' },
  { id: 'manage-company-lists', label: 'Manage Company Lists', permission: PERMISSIONS.MANAGE_COMPANY_LISTS, path: '/manage-company-lists' },
  { id: 'vendor-credits', label: 'Vendor Credits', public: true, path: '/vendor-credits' },
  { id: 'pinboard', label: 'Knowledge Hub', public: true, path: '/knowledge-hub/pins' },
  {
    id: 'data-correction',
    label: 'Data Correction',
    permission: [PERMISSIONS.DATA_CORRECTION_UPLOADER, PERMISSIONS.DATA_CORRECTION_APPROVER],
    path: '/data-correction',
  },
  {
    id: 'data-correction-logs',
    label: 'Data Correction Logs',
    permission: [PERMISSIONS.DATA_CORRECTION_UPLOADER, PERMISSIONS.DATA_CORRECTION_APPROVER],
    path: '/data-correction-logs',
  },
  { id: 'public-images', label: 'Public Images', permission: PERMISSIONS.MANAGE_PUBLIC_IMAGES, path: '/public-images' },
  { id: 'whats-new', label: 'Whats New', permission: PERMISSIONS.MANAGE_WHATS_NEW, path: '/whats-new' },
  { id: 'signup-blacklist', label: 'Signup BlackList', permission: PERMISSIONS.MANAGE_SIGNUP_BLACKLIST, path: '/signup-blacklist' },
  {
    id: 'skylar-threads',
    label: "Skylar's Threads",
    permission: PERMISSIONS.VIEW_SKYLAR_THREADS,
    externalUrl: SKYLAR_THREADS_URL,
  },
  { id: 'manage-users', label: 'Manage Users', permission: PERMISSIONS.MANAGE_USERS, admin: true, path: '/manage-users' },
  { id: 'audit-logs', label: 'Audit Logs', permission: PERMISSIONS.VIEW_AUDIT_LOGS, admin: true, path: '/audit-logs' },
];

const LeftNavBar = ({
  activeView,
  onViewChange,
  user,
  onLogout,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
  onMobileOpen,
}) => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { token } = antdThemeApi.useToken();

  const menuItems = useMemo(() => {
    return allMenuItems.filter((item) => {
      if (item.public) return true;
      if (item.permission) {
        const requiredPermissions = Array.isArray(item.permission) ? item.permission : [item.permission];
        return requiredPermissions.some((permission) => {
          if (!user || !user.permissions) return false;
          return user.permissions.includes(permission);
        });
      }
      return false;
    });
  }, [user]);

  const handleSelect = ({ key }) => {
    const item = allMenuItems.find((m) => m.id === key);
    if (!item) return;

    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      if (isMobile && onMobileClose) onMobileClose();
      return;
    }

    navigate(item.path);
    if (onViewChange) onViewChange(item.id);
    if (isMobile && onMobileClose) onMobileClose();
  };

  const antdMenuItems = useMemo(() => {
    const items = [];
    let adminDividerAdded = false;

    menuItems.forEach((item, index) => {
      if (item.admin && !adminDividerAdded) {
        const prev = menuItems[index - 1];
        if (!prev || !prev.admin) {
          items.push({ type: 'divider', key: 'admin-divider' });
          items.push({
            type: 'group',
            key: 'admin-group',
            label: 'Administration',
            children: [],
          });
          adminDividerAdded = true;
        }
      }

      const menuItem = {
        key: item.id,
        icon: ICON_MAP[item.id],
        label: item.externalUrl ? (
          <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.preventDefault()}>
            {item.label}
          </a>
        ) : (
          item.label
        ),
      };

      if (item.admin && items.length) {
        const group = items.find((i) => i.key === 'admin-group');
        if (group && Array.isArray(group.children)) {
          group.children.push(menuItem);
          return;
        }
      }
      items.push(menuItem);
    });

    return items;
  }, [menuItems]);

  const footer = (
    <Flex vertical gap={12} style={{ padding: '12px 16px 16px', flexShrink: 0 }}>
      <Divider style={{ margin: '0 0 4px', borderColor: 'rgba(255,255,255,0.12)' }} />
      {user && (
        <Flex gap={10} align="flex-start">
          <Avatar src={user.picture} icon={<UserOutlined />} size={36} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text strong style={{ color: 'rgba(255,255,255,0.85)', display: 'block', lineHeight: 1.3 }}>
              {user.name}
            </Text>
            <Text
              type="secondary"
              style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, wordBreak: 'break-all' }}
            >
              {user.email}
            </Text>
          </div>
        </Flex>
      )}

      <Flex align="center" justify="space-between">
        <Space size={8}>
          <BulbOutlined style={{ color: 'rgba(255,255,255,0.65)' }} />
          <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
            {isDarkMode ? 'Dark' : 'Light'}
          </Text>
        </Space>
        <Switch size="small" checked={isDarkMode} onChange={toggleTheme} />
      </Flex>

      <Button danger block icon={<LogoutOutlined />} onClick={onLogout}>
        Logout
      </Button>
    </Flex>
  );

  const navPanel = (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ padding: '16px 20px 8px', flexShrink: 0 }}>
        <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600, letterSpacing: '-0.02em' }}>
          HP Admin
        </Title>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 8 }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[activeView]}
          items={antdMenuItems}
          onClick={handleSelect}
          style={{ borderInlineEnd: 'none' }}
        />
      </div>

      {footer}
    </Flex>
  );

  if (isMobile) {
    return (
      <>
        <Layout.Header
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1001,
            height: 56,
            lineHeight: '56px',
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: token.colorBgElevated === token.colorBgContainer ? '#001529' : '#001529',
          }}
        >
          <Button
            type="text"
            aria-label="Open navigation menu"
            icon={<MenuOutlined style={{ color: '#fff', fontSize: 18 }} />}
            onClick={onMobileOpen}
          />
          <Text strong style={{ color: '#fff', fontSize: 16 }}>
            HP Admin
          </Text>
        </Layout.Header>
        <Drawer
          placement="left"
          open={mobileOpen}
          onClose={onMobileClose}
          width={SIDEBAR_WIDTH}
          styles={{
            body: { padding: 0 },
            header: { display: 'none' },
          }}
          closable={false}
        >
          <div style={{ height: '100%', background: '#001529' }}>{navPanel}</div>
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      width={SIDEBAR_WIDTH}
      theme="dark"
      style={{
        overflow: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000,
      }}
    >
      {navPanel}
    </Sider>
  );
};

export default LeftNavBar;
